import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  acquire,
  getOrFetch,
  peek,
  release,
  subscribe,
} from '../services/auditImageCache';

/**
 * Lazy-load an audit recognition image when visible, with session cache reuse.
 *
 * @param {string|null|undefined} auditId
 * @param {{ enabled?: boolean, rootMargin?: string, triggerOnce?: boolean }} [options]
 * @returns {{ ref: Function, url: string|null|undefined, status: 'idle'|'loading'|'ready'|'error' }}
 */
export function useAuditImage(auditId, options = {}) {
  const {
    enabled = true,
    rootMargin = '200px',
    triggerOnce = true,
  } = options;

  const shouldObserve = Boolean(auditId) && enabled;
  const { ref, inView } = useInView({
    triggerOnce,
    rootMargin,
    skip: !shouldObserve,
  });

  const [, tick] = useState(0);
  const bump = useCallback(() => tick((n) => n + 1), []);

  useEffect(() => {
    if (!auditId) return undefined;
    acquire(auditId);
    const unsubscribe = subscribe(auditId, bump);
    return () => {
      unsubscribe();
      release(auditId);
    };
  }, [auditId, bump]);

  useEffect(() => {
    if (!shouldObserve || !inView || !auditId) return undefined;
    let cancelled = false;
    getOrFetch(auditId).finally(() => {
      if (!cancelled) bump();
    });
    return () => {
      cancelled = true;
    };
  }, [auditId, shouldObserve, inView, bump]);

  const url = auditId ? peek(auditId) : undefined;
  const status = !auditId
    ? 'idle'
    : url === undefined
      ? 'loading'
      : url === null
        ? 'error'
        : 'ready';

  return { ref, url, status, inView };
}

export default useAuditImage;
